import type { FC, ReactNode } from 'react'

import { BatteryCharging, ShieldCheck } from 'lucide-react'
import { LogoMark } from '@/components/generals/logo'
import { areaToBaselinePath, linePath } from '@/lib/chart'
import { dashboardRanges } from '@/constants/dashboard'

const WIDTH = 100
const HEIGHT = 60

const PhoneForecast: FC = (): ReactNode => {
    const points = dashboardRanges[0].points
    const values = points.map((point) => point.forecast)
    const min = Math.min(...values) * 0.94
    const max = Math.max(...values) * 1.02

    const line = values.map((value, index) => ({
        x: (index / (values.length - 1)) * WIDTH,
        y: HEIGHT - ((value - min) / (max - min)) * HEIGHT
    }))

    const peakIndex = values.indexOf(Math.max(...values))
    const peak = line[peakIndex]

    return (
        <div
            role='img'
            aria-label='Gridora mobile app showing a forecast peak load of 2,914 megawatts at 18:00'
            className='w-[248px] rounded-[2.75rem] bg-foreground p-2.5 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.8)] ring-1 ring-white/10 sm:w-[268px]'
        >
            <div className='relative overflow-hidden rounded-[2.25rem] bg-forest bg-gradient-to-b from-primary/35 to-transparent px-4 pb-6 pt-3 text-white'>
                <div className='flex items-center justify-between px-2 text-[11px] font-semibold'>
                    <span>9:41</span>
                    <span className='h-5 w-20 rounded-full bg-black' />
                    <span className='flex items-center gap-1'>
                        <span className='flex items-end gap-px'>
                            <span className='h-1.5 w-[3px] rounded-sm bg-white' />
                            <span className='h-2 w-[3px] rounded-sm bg-white' />
                            <span className='h-2.5 w-[3px] rounded-sm bg-white' />
                        </span>
                        <span className='h-2.5 w-5 rounded-[3px] border border-white/70 p-px'>
                            <span className='block h-full w-3/4 rounded-[1px] bg-white' />
                        </span>
                    </span>
                </div>

                <div className='mt-5 flex items-center justify-between'>
                    <span className='flex items-center gap-2 text-xs font-medium text-white/80'>
                        <LogoMark tone='light' className='size-5' />
                        Load forecast
                    </span>

                    <span className='rounded-full bg-white/10 px-2.5 py-1 text-[10px] font-medium text-white/80'>
                        Today
                    </span>
                </div>

                <p className='mt-5 font-heading text-[2.6rem] font-light leading-none tracking-tight'>
                    2,914
                    <span className='ml-1 text-sm font-normal text-white/60'>MW</span>
                </p>

                <p className='mt-2 flex items-center gap-1.5 text-[11px] text-white/70'>
                    <span className='size-1.5 rounded-full bg-sun' />
                    Peak expected at 18:00
                </p>

                <div className='relative mt-5 h-28'>
                    <svg
                        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                        preserveAspectRatio='none'
                        className='absolute inset-0 size-full overflow-visible'
                    >
                        <path d={areaToBaselinePath(line, HEIGHT)} className='fill-leaf/15' />
                        <path
                            d={linePath(line)}
                            fill='none'
                            strokeWidth={2}
                            strokeLinecap='round'
                            vectorEffect='non-scaling-stroke'
                            className='stroke-leaf'
                        />
                    </svg>

                    <span
                        className='absolute size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-sun ring-2 ring-forest'
                        style={{ left: `${(peak.x / WIDTH) * 100}%`, top: `${(peak.y / HEIGHT) * 100}%` }}
                    />
                </div>

                <div className='mt-2 flex justify-between text-[10px] text-white/45'>
                    <span>00:00</span>
                    <span>06:00</span>
                    <span>12:00</span>
                    <span>18:00</span>
                </div>

                <div className='mt-5 grid grid-cols-2 gap-2'>
                    <div className='rounded-2xl bg-white/[0.08] p-3'>
                        <ShieldCheck className='size-4 text-leaf' aria-hidden='true' />
                        <p className='mt-2 text-lg font-medium leading-none'>94%</p>
                        <p className='mt-1 text-[10px] text-white/60'>Confidence</p>
                    </div>

                    <div className='rounded-2xl bg-white/[0.08] p-3'>
                        <BatteryCharging className='size-4 text-leaf' aria-hidden='true' />
                        <p className='mt-2 text-lg font-medium leading-none'>680</p>
                        <p className='mt-1 text-[10px] text-white/60'>MWh ready</p>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default PhoneForecast
